import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Placement, placeHoverWindow } from './clampedTextPlacement';

/**
 * Langer Text, auf wenige Zeilen gekappt — der volle Text erscheint beim
 * Ueberfahren in einem Fenster daneben.
 *
 * Anlass: eine Angebotsposition mit langer Leistungsbeschreibung landet als
 * Titel im Projekt-LV, und „Leistung & Budget" zeigte sie dann seitenlang.
 * Das Fenster haengt per Portal am body, damit weder der scrollende
 * Kartenbereich noch ein Dialog es abschneidet; es erscheint nur, wenn der
 * Text wirklich gekappt ist. Im DOM steht immer der ganze Text — Kappen ist
 * reine Darstellung, Screenreader und Suche sehen alles.
 */

const CLAMP_CLASS = { 1: 'line-clamp-1', 2: 'line-clamp-2', 3: 'line-clamp-3' } as const;

const SHOW_DELAY_MS = 250;
// Kurze Gnadenfrist, damit die Maus vom Text ins Fenster wechseln (und dort
// scrollen) kann, ohne dass es dazwischen zugeht.
const HIDE_DELAY_MS = 150;

interface Props {
  text: string;
  lines?: keyof typeof CLAMP_CLASS;
  className?: string;
  as?: 'div' | 'span' | 'h3' | 'h4';
}

export const ClampedText: React.FC<Props> = ({ text, lines = 2, className = '', as: Tag = 'div' }) => {
  const textRef = useRef<HTMLElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const showTimer = useRef<number | undefined>(undefined);
  const hideTimer = useRef<number | undefined>(undefined);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [clamped, setClamped] = useState(false);

  const clearTimers = () => {
    window.clearTimeout(showTimer.current);
    window.clearTimeout(hideTimer.current);
  };

  const hide = useCallback(() => {
    clearTimers();
    setPlacement(null);
  }, []);

  const onEnter = () => {
    const el = textRef.current;
    if (!el) return;
    window.clearTimeout(hideTimer.current);
    const isClamped = el.scrollHeight > el.clientHeight + 1;
    setClamped(isClamped);
    if (!isClamped || placement) return;
    showTimer.current = window.setTimeout(() => {
      if (textRef.current) setPlacement(placeHoverWindow(textRef.current.getBoundingClientRect(), window.innerWidth, window.innerHeight));
    }, SHOW_DELAY_MS);
  };

  const onLeave = () => {
    window.clearTimeout(showTimer.current);
    hideTimer.current = window.setTimeout(() => setPlacement(null), HIDE_DELAY_MS);
  };

  // Das Fenster steht fest im Viewport — scrollt darunter etwas weg, passt
  // es nicht mehr zum Text und geht zu. Scrollen im Fenster selbst nicht.
  useEffect(() => {
    if (!placement) return;
    const onScroll = (e: Event) => {
      if (popRef.current && e.target instanceof Node && popRef.current.contains(e.target)) return;
      hide();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', hide);
    };
  }, [placement, hide]);

  useEffect(() => clearTimers, []);

  return (
    <>
      <Tag
        ref={textRef as React.Ref<never>}
        className={`${CLAMP_CLASS[lines]} ${className}`}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        // Leerer title verdraengt den title eines umgebenden Elements
        // (z. B. „Details dieser Position anzeigen") — sonst stuenden
        // Browser-Tooltip und Fenster uebereinander.
        title={clamped ? '' : undefined}
      >
        {text}
      </Tag>
      {placement && createPortal(
        <div
          ref={popRef}
          role="tooltip"
          className="fixed z-[400] overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 text-xs font-normal leading-relaxed text-slate-700 shadow-xl whitespace-pre-line break-words animate-fade-in"
          style={{ left: placement.left, width: placement.width, top: placement.top, bottom: placement.bottom, maxHeight: placement.maxHeight }}
          onMouseEnter={() => window.clearTimeout(hideTimer.current)}
          onMouseLeave={onLeave}
          // React reicht Klicks aus dem Portal an die Vorfahren im Baum weiter —
          // ein Klick ins Fenster soll nicht die Zeile darunter buchen.
          onClick={(e) => e.stopPropagation()}
        >
          {text}
        </div>,
        document.body,
      )}
    </>
  );
};
